/**
 * KizunaAI vocabulary guide: custom vocabulary (terms, preferred translations)
 * and session background.
 *
 * Screenshots were taken from the Sokuji v0.41.1 desktop app.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  pageTitle: 'Custom Vocabulary and Session Background',
  backLink: 'Back to KizunaAI Quick Start',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Overview',
    content:
      'Names, product names and jargon are what real-time translation gets wrong most often. KizunaAI lets you tell it, before the meeting, which words to expect, how to translate specific terms, and what the meeting is about. All three fields are optional, in <strong>Settings → Advanced → Provider</strong>, and apply from the next session. They belong to the Soniox engine; the Qwen engine has Preferred Translations only (see the FAQ). The example is a call between 中文 and English.',
  },
  steps: [
    {
      title: 'Open the fields',
      content:
        'Open <strong>Settings</strong>, switch to <strong>Advanced</strong> with the button at the top right of the panel, and choose the <strong>Provider</strong> tab. Scroll to <strong>Custom Vocabulary</strong> and <strong>Session Background</strong>.',
      screenshot: '01-vocabulary-and-background.png',
    },
    {
      title: 'List the terms',
      content:
        'Under <strong>Terms</strong>, write one name or term per line: people, companies, products, acronyms and jargon. This helps recognition catch words it would otherwise mishear.',
    },
    {
      title: 'Set preferred translations',
      content:
        'Under <strong>Preferred Translations</strong>, write <code>source=target</code>, one per line, to say how a term should be translated, for example <code>季度复盘=quarterly review</code>. It is a preference, not a guaranteed replacement: names with an established rendering work best, while common words may keep the model’s own wording.',
      tip: 'Entries work in one direction. In Both mode, add the reverse line as well (quarterly review=季度复盘) if you want the term handled both ways.',
    },
    {
      title: 'Describe the meeting',
      content:
        'Under <strong>Session Background</strong>, paste the agenda, the topic or a few notes about the meeting. The context helps recognition and translation follow the subject.',
    },
    {
      title: 'Start the session',
      content:
        'Click <strong>Start</strong>. The vocabulary and background apply from the session you start next; a session already running keeps what it started with.',
    },
  ],
  tips: {
    title: 'Tips',
    items: [
      'Keep the lists short and specific to the meeting: a few dozen real names help more than a long generic glossary.',
      'Each field holds up to 4,000 characters. If the combined context is too large, the session background is shortened first.',
      'The fields are saved on this device, so the next meeting starts with them. Update them before each meeting.',
    ],
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'Why was my preferred translation not used?',
        answer:
          'Preferred translations steer the model rather than replace words mechanically. Established names work best; very common words may keep the model’s own wording. Check that the line is written in the direction being translated.',
      },
      {
        question: 'Do I need both fields?',
        answer: 'No. Each helps on its own. Terms fix recognition of specific words; the background helps with the subject as a whole.',
      },
      {
        question: 'Which of these does the Qwen engine have?',
        answer: 'Preferred Translations only, written the same way: source=target, one per line, in one direction. Qwen has no Terms list and no Session Background, so its Custom Vocabulary block shows that one field.',
      },
    ],
  },
  troubleshooting: {
    title: 'Troubleshooting',
    content:
      'Still stuck? Send us feedback from the account menu, or write to <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. You can also ask in <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const zh: TutorialData = {
  pageTitle: '自定义词表与会话背景',
  backLink: '返回 KizunaAI 快速入门',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概述',
    content:
      '人名、产品名和行业术语是实时翻译最常出错的地方。KizunaAI 允许你在会议开始前告诉它：会出现哪些词、特定术语该怎么翻译，以及会议的主题是什么。这三个字段都是可选的，位于<strong>设置 → 高级 → 提供商</strong>中，从下一次会话开始生效。它们属于 Soniox 引擎；Qwen 引擎只有指定译法（见常见问题）。示例是一场中文和英语之间的通话。',
  },
  steps: [
    {
      title: '打开这些字段',
      content:
        '打开<strong>设置</strong>，用面板右上角的按钮切换到<strong>高级</strong>，然后选择<strong>提供商</strong>标签页。滚动到<strong>自定义词表</strong>和<strong>会话背景</strong>。',
      screenshot: '01-vocabulary-and-background.png',
    },
    {
      title: '列出术语',
      content:
        '在<strong>术语</strong>下，每行写一个名称或术语：人名、公司、产品、缩写和行业术语。这有助于识别出原本容易听错的词。',
    },
    {
      title: '设置指定译法',
      content:
        '在<strong>指定译法</strong>下，每行写一条 <code>source=target</code>，说明某个术语应如何翻译，例如 <code>季度复盘=quarterly review</code>。这只是一种偏好，并不保证一定替换：有固定译法的名称效果最好，而常用词可能仍保留模型自己的措辞。',
      tip: '每一条只对一个方向生效。在两者模式下，如果希望两个方向都这样处理该术语，请再加一行反向的条目（quarterly review=季度复盘）。',
    },
    {
      title: '描述会议',
      content:
        '在<strong>会话背景</strong>下，粘贴会议议程、主题或几条关于会议的说明。这些上下文有助于识别和翻译紧跟会议主题。',
    },
    {
      title: '开始会话',
      content:
        '点击<strong>开始</strong>。词表和背景从你下一次开始的会话起生效；已经在运行的会话会沿用它开始时的设置。',
    },
  ],
  tips: {
    title: '提示',
    items: [
      '列表要简短，并针对这次会议：几十个真实的名称，比一份冗长的通用词汇表更有用。',
      '每个字段最多可容纳 4,000 个字符。如果合并后的上下文过长，会优先缩短会话背景。',
      '这些字段保存在本设备上，下一次会议会沿用它们。请在每次会议前更新。',
    ],
  },
  faq: {
    title: '常见问题',
    items: [
      {
        question: '为什么没有使用我指定的译法？',
        answer:
          '指定译法是在引导模型，而不是机械地替换词语。有固定译法的名称效果最好；非常常用的词可能仍保留模型自己的措辞。请检查该行是否按正在翻译的方向书写。',
      },
      {
        question: '两个字段都需要填吗？',
        answer: '不需要。每个字段单独使用都有帮助。术语用于纠正特定词语的识别；背景有助于把握整体主题。',
      },
      {
        question: 'Qwen 引擎有其中哪些字段？',
        answer: '只有指定译法，写法相同：source=target，每行一条，只对一个方向生效。Qwen 没有术语列表，也没有会话背景，所以它的自定义词表区块只显示这一个字段。',
      },
    ],
  },
  troubleshooting: {
    title: '故障排除',
    content:
      '仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>。你也可以在 <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> 中提问。',
  },
};

const ja: TutorialData = {
  pageTitle: 'カスタム語彙とセッションの背景',
  backLink: 'KizunaAI クイックスタートに戻る',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概要',
    content:
      '人名、製品名、専門用語は、リアルタイム翻訳がもっとも間違えやすいところです。KizunaAI では、会議の前に、出てくる言葉、特定の用語の訳し方、会議の内容を伝えておけます。3 つの項目はいずれも任意で、<strong>設定 → 詳細 → プロバイダー</strong>にあり、次のセッションから適用されます。これらは Soniox エンジンの項目で、Qwen エンジンにあるのは優先する訳語だけです（よくある質問を参照）。例は中国語と英語の通話です。',
  },
  steps: [
    {
      title: '項目を開く',
      content:
        '<strong>設定</strong>を開き、パネル右上のボタンで<strong>詳細</strong>に切り替えて、<strong>プロバイダー</strong>タブを選びます。<strong>カスタム語彙</strong>と<strong>セッションの背景</strong>までスクロールします。',
      screenshot: '01-vocabulary-and-background.png',
    },
    {
      title: '用語を書き出す',
      content:
        '<strong>用語</strong>に、人名、会社名、製品名、略語、専門用語などを 1 行に 1 つずつ書きます。これにより、ほかの言葉と聞き間違えやすい語も正しく認識されやすくなります。',
    },
    {
      title: '優先する訳語を設定する',
      content:
        '<strong>優先する訳語</strong>に <code>source=target</code> の形で 1 行に 1 つずつ書き、用語の訳し方を指定します。例：<code>季度复盘=quarterly review</code>。これは優先の指定であり、必ず置き換わるわけではありません。定訳のある名前ほどよく効き、一般的な語はモデル自身の言い回しのままになることがあります。',
      tip: '各行は一方向にだけ効きます。両方モードで用語を両方向に適用したい場合は、逆向きの行（quarterly review=季度复盘）も追加してください。',
    },
    {
      title: '会議の内容を伝える',
      content:
        '<strong>セッションの背景</strong>に、議題、テーマ、会議についての簡単なメモを貼り付けます。この文脈があると、認識と翻訳が話題に沿いやすくなります。',
    },
    {
      title: 'セッションを開始する',
      content:
        '<strong>開始</strong>をクリックします。語彙と背景は、次に開始するセッションから適用されます。すでに実行中のセッションは、開始時の内容のまま続きます。',
    },
  ],
  tips: {
    title: 'ヒント',
    items: [
      'リストは短く、その会議に合わせた内容にしましょう。長い汎用の用語集より、実際に出てくる名前が数十個あるほうが役立ちます。',
      '各項目には最大 4,000 文字まで入力できます。文脈全体が大きすぎる場合は、セッションの背景から先に短縮されます。',
      '項目はこの端末に保存されるので、次の会議もその内容で始まります。会議のたびに更新してください。',
    ],
  },
  faq: {
    title: 'よくある質問',
    items: [
      {
        question: '優先する訳語が使われなかったのはなぜですか？',
        answer:
          '優先する訳語は、語を機械的に置き換えるのではなく、モデルの訳し方を方向づけるものです。定訳のある名前ほどよく効き、ごく一般的な語はモデル自身の言い回しのままになることがあります。翻訳される方向で行を書いているか確認してください。',
      },
      {
        question: '両方の項目が必要ですか？',
        answer: 'いいえ。それぞれ単独でも役立ちます。用語は特定の語の認識を正し、背景は話題全体の理解を助けます。',
      },
      {
        question: 'Qwen エンジンにはこのうちどれがありますか？',
        answer: '優先する訳語だけで、書き方は同じです。source=target の形で 1 行に 1 つ、一方向に指定します。Qwen には用語リストもセッションの背景もないため、カスタム語彙のブロックにはこの 1 つの項目だけが表示されます。',
      },
    ],
  },
  troubleshooting: {
    title: 'トラブルシューティング',
    content:
      'それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href="mailto:support@kizuna.ai">support@kizuna.ai</a> までご連絡ください。<a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> で質問することもできます。',
  },
};

const ko: TutorialData = {
  pageTitle: '맞춤 어휘와 세션 배경',
  backLink: 'KizunaAI 빠른 시작으로 돌아가기',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '개요',
    content:
      '사람 이름, 제품명, 전문 용어는 실시간 번역이 가장 자주 틀리는 부분입니다. KizunaAI에서는 회의 전에 어떤 단어가 나올지, 특정 용어를 어떻게 번역할지, 회의가 무엇에 관한 것인지 미리 알려 줄 수 있습니다. 세 항목 모두 선택 사항이며 <strong>설정 → 고급 → 제공자</strong>에 있고, 다음 세션부터 적용됩니다. 이 항목들은 Soniox 엔진용이며, Qwen 엔진에는 선호하는 번역만 있습니다(자주 묻는 질문 참조). 예시는 중국어와 영어로 진행되는 통화입니다.',
  },
  steps: [
    {
      title: '항목 열기',
      content:
        '<strong>설정</strong>을 열고 패널 오른쪽 위의 버튼으로 <strong>고급</strong>으로 전환한 다음 <strong>제공자</strong> 탭을 선택하세요. <strong>맞춤 어휘</strong>와 <strong>세션 배경</strong>까지 스크롤하세요.',
      screenshot: '01-vocabulary-and-background.png',
    },
    {
      title: '용어 나열하기',
      content:
        '<strong>용어</strong>에 사람, 회사, 제품, 약어, 전문 용어 등 이름이나 용어를 한 줄에 하나씩 적으세요. 그러면 잘못 알아듣기 쉬운 단어도 제대로 인식하는 데 도움이 됩니다.',
    },
    {
      title: '선호하는 번역 설정',
      content:
        '<strong>선호하는 번역</strong>에 <code>source=target</code> 형식으로 한 줄에 하나씩 적어 용어를 어떻게 번역할지 지정하세요. 예: <code>季度复盘=quarterly review</code>. 이는 선호 사항일 뿐 반드시 바뀐다는 보장은 없습니다. 정해진 번역이 있는 이름이 가장 잘 적용되고, 일반적인 단어는 모델 자체의 표현이 유지될 수 있습니다.',
      tip: '각 항목은 한 방향으로만 적용됩니다. 모두 모드에서 용어를 양방향으로 처리하려면 반대 방향 줄(quarterly review=季度复盘)도 추가하세요.',
    },
    {
      title: '회의 설명하기',
      content:
        '<strong>세션 배경</strong>에 안건, 주제 또는 회의에 관한 간단한 메모를 붙여 넣으세요. 이 맥락이 인식과 번역이 주제를 따라가는 데 도움이 됩니다.',
    },
    {
      title: '세션 시작',
      content:
        '<strong>시작</strong>을 클릭하세요. 어휘와 배경은 다음에 시작하는 세션부터 적용되며, 이미 실행 중인 세션은 시작할 때의 설정을 그대로 사용합니다.',
    },
  ],
  tips: {
    title: '팁',
    items: [
      '목록은 짧게, 해당 회의에 맞게 작성하세요. 긴 일반 용어집보다 실제 이름 수십 개가 더 도움이 됩니다.',
      '각 항목에는 최대 4,000자까지 입력할 수 있습니다. 전체 맥락이 너무 길면 세션 배경부터 줄어듭니다.',
      '항목은 이 기기에 저장되므로 다음 회의도 그 내용으로 시작합니다. 회의마다 업데이트하세요.',
    ],
  },
  faq: {
    title: '자주 묻는 질문',
    items: [
      {
        question: '선호하는 번역이 적용되지 않은 이유는 무엇인가요?',
        answer:
          '선호하는 번역은 단어를 기계적으로 바꾸는 것이 아니라 모델을 유도합니다. 정해진 번역이 있는 이름이 가장 잘 적용되고, 아주 흔한 단어는 모델 자체의 표현이 유지될 수 있습니다. 번역되는 방향으로 줄을 작성했는지 확인하세요.',
      },
      {
        question: '두 항목을 모두 채워야 하나요?',
        answer: '아니요. 각각 따로 써도 도움이 됩니다. 용어는 특정 단어의 인식을 바로잡고, 배경은 주제 전체를 이해하는 데 도움을 줍니다.',
      },
      {
        question: 'Qwen 엔진에는 이 중 무엇이 있나요?',
        answer: '선호하는 번역만 있으며, 작성 방식도 같습니다. 한 줄에 하나씩 source=target 형식으로 적고, 한 방향으로만 적용됩니다. Qwen에는 용어 목록도 세션 배경도 없으므로, 맞춤 어휘 블록에는 그 항목 하나만 표시됩니다.',
      },
    ],
  },
  troubleshooting: {
    title: '문제 해결',
    content:
      '그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>에서 질문할 수도 있습니다.',
  },
};

const es: TutorialData = {
  pageTitle: 'Vocabulario personalizado y contexto de la sesión',
  backLink: 'Volver al inicio rápido de KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Descripción general',
    content:
      'Los nombres propios, los nombres de productos y la jerga son lo que la traducción en tiempo real falla con más frecuencia. KizunaAI te permite indicarle, antes de la reunión, qué palabras esperar, cómo traducir términos concretos y de qué trata la reunión. Los tres campos son opcionales, están en <strong>Configuración → Avanzado → Proveedor</strong> y se aplican a partir de la siguiente sesión. Pertenecen al motor de Soniox; el motor de Qwen solo tiene Traducciones preferidas (consulta las preguntas frecuentes). El ejemplo es una llamada entre 中文 e inglés.',
  },
  steps: [
    {
      title: 'Abre los campos',
      content:
        'Abre <strong>Configuración</strong>, cambia a <strong>Avanzado</strong> con el botón de la parte superior derecha del panel y elige la pestaña <strong>Proveedor</strong>. Desplázate hasta <strong>Vocabulario personalizado</strong> y <strong>Contexto de la sesión</strong>.',
      screenshot: '01-vocabulary-and-background.png',
    },
    {
      title: 'Enumera los términos',
      content:
        'En <strong>Términos</strong>, escribe un nombre o término por línea: personas, empresas, productos, siglas y jerga. Así el reconocimiento capta palabras que de otro modo oiría mal.',
    },
    {
      title: 'Define traducciones preferidas',
      content:
        'En <strong>Traducciones preferidas</strong>, escribe <code>source=target</code>, una por línea, para indicar cómo debe traducirse un término, por ejemplo <code>季度复盘=quarterly review</code>. Es una preferencia, no un reemplazo garantizado: los nombres con una traducción establecida funcionan mejor, mientras que las palabras comunes pueden conservar la formulación del propio modelo.',
      tip: 'Cada entrada funciona en una sola dirección. En el modo Ambos, añade también la línea inversa (quarterly review=季度复盘) si quieres que el término se trate en los dos sentidos.',
    },
    {
      title: 'Describe la reunión',
      content:
        'En <strong>Contexto de la sesión</strong>, pega el orden del día, el tema o unas notas sobre la reunión. Ese contexto ayuda a que el reconocimiento y la traducción sigan el tema.',
    },
    {
      title: 'Inicia la sesión',
      content:
        'Haz clic en <strong>Iniciar</strong>. El vocabulario y el contexto se aplican a partir de la próxima sesión que inicies; una sesión que ya está en marcha conserva lo que tenía al empezar.',
    },
  ],
  tips: {
    title: 'Consejos',
    items: [
      'Mantén las listas cortas y específicas de la reunión: unas decenas de nombres reales ayudan más que un glosario genérico largo.',
      'Cada campo admite hasta 4000 caracteres. Si el contexto combinado es demasiado grande, se acorta primero el contexto de la sesión.',
      'Los campos se guardan en este dispositivo, así que la próxima reunión empieza con ellos. Actualízalos antes de cada reunión.',
    ],
  },
  faq: {
    title: 'Preguntas frecuentes',
    items: [
      {
        question: '¿Por qué no se usó mi traducción preferida?',
        answer:
          'Las traducciones preferidas orientan al modelo en lugar de sustituir palabras mecánicamente. Los nombres establecidos funcionan mejor; las palabras muy comunes pueden conservar la formulación del propio modelo. Comprueba que la línea esté escrita en la dirección en la que se traduce.',
      },
      {
        question: '¿Necesito los dos campos?',
        answer: 'No. Cada uno ayuda por sí solo. Los términos corrigen el reconocimiento de palabras concretas; el contexto ayuda con el tema en su conjunto.',
      },
      {
        question: '¿Cuáles de estos tiene el motor de Qwen?',
        answer: 'Solo Traducciones preferidas, escritas del mismo modo: source=target, una por línea, en una sola dirección. Qwen no tiene lista de Términos ni Contexto de la sesión, así que su bloque Vocabulario personalizado muestra ese único campo.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solución de problemas',
    content:
      '¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. También puedes preguntar en <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const fr: TutorialData = {
  pageTitle: 'Vocabulaire personnalisé et contexte de la session',
  backLink: 'Retour au démarrage rapide KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Aperçu',
    content:
      'Les noms de personnes, les noms de produits et le jargon sont ce que la traduction en temps réel rate le plus souvent. KizunaAI vous permet de lui indiquer, avant la réunion, les mots à attendre, la façon de traduire certains termes et le sujet de la réunion. Les trois champs sont facultatifs, se trouvent dans <strong>Paramètres → Avancé → Fournisseur</strong> et s’appliquent à partir de la session suivante. Ils appartiennent au moteur Soniox ; le moteur Qwen n’a que les Traductions préférées (voir la FAQ). L’exemple est un appel entre le 中文 et l’anglais.',
  },
  steps: [
    {
      title: 'Ouvrez les champs',
      content:
        'Ouvrez les <strong>Paramètres</strong>, passez en <strong>Avancé</strong> avec le bouton en haut à droite du panneau, puis choisissez l’onglet <strong>Fournisseur</strong>. Faites défiler jusqu’à <strong>Vocabulaire personnalisé</strong> et <strong>Contexte de la session</strong>.',
      screenshot: '01-vocabulary-and-background.png',
    },
    {
      title: 'Listez les termes',
      content:
        'Sous <strong>Termes</strong>, écrivez un nom ou un terme par ligne : personnes, entreprises, produits, sigles et jargon. Cela aide la reconnaissance à saisir des mots qu’elle entendrait mal autrement.',
    },
    {
      title: 'Définissez les traductions préférées',
      content:
        'Sous <strong>Traductions préférées</strong>, écrivez <code>source=target</code>, une par ligne, pour indiquer comment traduire un terme, par exemple <code>季度复盘=quarterly review</code>. C’est une préférence, pas un remplacement garanti : les noms dotés d’une traduction établie fonctionnent le mieux, tandis que les mots courants peuvent garder la formulation du modèle.',
      tip: 'Chaque entrée ne fonctionne que dans un sens. En mode Les deux, ajoutez aussi la ligne inverse (quarterly review=季度复盘) si vous voulez que le terme soit traité dans les deux sens.',
    },
    {
      title: 'Décrivez la réunion',
      content:
        'Sous <strong>Contexte de la session</strong>, collez l’ordre du jour, le sujet ou quelques notes sur la réunion. Ce contexte aide la reconnaissance et la traduction à suivre le sujet.',
    },
    {
      title: 'Démarrez la session',
      content:
        'Cliquez sur <strong>Démarrer</strong>. Le vocabulaire et le contexte s’appliquent à partir de la prochaine session que vous démarrez ; une session déjà en cours garde les réglages avec lesquels elle a démarré.',
    },
  ],
  tips: {
    title: 'Conseils',
    items: [
      'Gardez des listes courtes et propres à la réunion : quelques dizaines de vrais noms aident davantage qu’un long glossaire générique.',
      'Chaque champ peut contenir jusqu’à 4 000 caractères. Si le contexte combiné est trop volumineux, le contexte de la session est raccourci en premier.',
      'Les champs sont enregistrés sur cet appareil : la réunion suivante démarre donc avec eux. Mettez-les à jour avant chaque réunion.',
    ],
  },
  faq: {
    title: 'Questions fréquentes',
    items: [
      {
        question: 'Pourquoi ma traduction préférée n’a-t-elle pas été utilisée ?',
        answer:
          'Les traductions préférées orientent le modèle au lieu de remplacer les mots mécaniquement. Les noms établis fonctionnent le mieux ; les mots très courants peuvent garder la formulation du modèle. Vérifiez que la ligne est écrite dans le sens de la traduction en cours.',
      },
      {
        question: 'Ai-je besoin des deux champs ?',
        answer: 'Non. Chacun aide à lui seul. Les termes corrigent la reconnaissance de mots précis ; le contexte aide pour le sujet dans son ensemble.',
      },
      {
        question: 'Lesquels de ces champs le moteur Qwen propose-t-il ?',
        answer: 'Seulement les Traductions préférées, écrites de la même façon : source=target, une par ligne, dans un seul sens. Qwen n’a ni liste de Termes ni Contexte de la session ; son bloc Vocabulaire personnalisé n’affiche donc que ce champ.',
      },
    ],
  },
  troubleshooting: {
    title: 'Dépannage',
    content:
      'Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ar: TutorialData = {
  pageTitle: 'المفردات المخصصة وخلفية الجلسة',
  backLink: 'العودة إلى البدء السريع مع KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'نظرة عامة',
    content:
      'الأسماء وأسماء المنتجات والمصطلحات المتخصصة هي أكثر ما تخطئ فيه الترجمة الفورية. يتيح لك KizunaAI أن تخبره قبل الاجتماع بالكلمات المتوقعة، وبطريقة ترجمة مصطلحات معيّنة، وبموضوع الاجتماع. الحقول الثلاثة كلها اختيارية، وتوجد في <strong>الإعدادات ← متقدم ← المزود</strong>، وتُطبَّق بدءًا من الجلسة التالية. وهي خاصة بمحرك Soniox؛ أما محرك Qwen فلديه «الترجمات المفضّلة» فقط (انظر الأسئلة الشائعة). المثال مكالمة بين 中文 والإنجليزية.',
  },
  steps: [
    {
      title: 'افتح الحقول',
      content:
        'افتح <strong>الإعدادات</strong>، وانتقل إلى <strong>متقدم</strong> بالزر الموجود أعلى يمين اللوحة، ثم اختر علامة التبويب <strong>المزود</strong>. مرّر إلى <strong>مفردات مخصصة</strong> و<strong>خلفية الجلسة</strong>.',
      screenshot: '01-vocabulary-and-background.png',
    },
    {
      title: 'اكتب المصطلحات',
      content:
        'تحت <strong>المصطلحات</strong>، اكتب اسمًا أو مصطلحًا واحدًا في كل سطر: أشخاص وشركات ومنتجات واختصارات ومصطلحات متخصصة. يساعد ذلك التعرّف على الكلام على التقاط كلمات كان سيُسيء سماعها لولا ذلك.',
    },
    {
      title: 'حدّد الترجمات المفضّلة',
      content:
        'تحت <strong>الترجمات المفضّلة</strong>، اكتب <code>source=target</code>، واحدًا في كل سطر، لتحدد كيف يُترجَم مصطلح ما، مثل <code>季度复盘=quarterly review</code>. إنها تفضيل لا استبدال مضمون: تعمل الأسماء ذات الترجمة المعتمدة بشكل أفضل، بينما قد تحتفظ الكلمات الشائعة بصياغة النموذج نفسه.',
      tip: 'يعمل كل سطر في اتجاه واحد. في وضع الكل، أضف أيضًا السطر المعاكس (quarterly review=季度复盘) إذا أردت معالجة المصطلح في الاتجاهين.',
    },
    {
      title: 'صِف الاجتماع',
      content:
        'تحت <strong>خلفية الجلسة</strong>، الصق جدول الأعمال أو الموضوع أو بعض الملاحظات عن الاجتماع. يساعد هذا السياق التعرّف على الكلام والترجمة على مواكبة الموضوع.',
    },
    {
      title: 'ابدأ الجلسة',
      content:
        'انقر على <strong>ابدأ</strong>. تُطبَّق المفردات والخلفية بدءًا من الجلسة التي تبدؤها بعد ذلك؛ أما الجلسة الجارية بالفعل فتحتفظ بما بدأت به.',
    },
  ],
  tips: {
    title: 'نصائح',
    items: [
      'اجعل القوائم قصيرة وخاصة بالاجتماع: بضع عشرات من الأسماء الحقيقية تفيد أكثر من مسرد عام طويل.',
      'يتسع كل حقل لما يصل إلى 4,000 حرف. إذا كان السياق المجمَّع كبيرًا جدًا، تُختصَر خلفية الجلسة أولًا.',
      'تُحفَظ الحقول على هذا الجهاز، فيبدأ الاجتماع التالي بها. حدّثها قبل كل اجتماع.',
    ],
  },
  faq: {
    title: 'الأسئلة الشائعة',
    items: [
      {
        question: 'لماذا لم تُستخدَم ترجمتي المفضّلة؟',
        answer:
          'توجّه الترجمات المفضّلة النموذج بدلًا من استبدال الكلمات آليًا. تعمل الأسماء المعتمدة بشكل أفضل؛ وقد تحتفظ الكلمات الشائعة جدًا بصياغة النموذج نفسه. تحقّق من أن السطر مكتوب في اتجاه الترجمة الجارية.',
      },
      {
        question: 'هل أحتاج إلى الحقلين كليهما؟',
        answer: 'لا. كلٌّ منهما مفيد وحده. تصحّح المصطلحات التعرّف على كلمات بعينها؛ وتساعد الخلفية على فهم الموضوع ككل.',
      },
      {
        question: 'أيٌّ من هذه يتوفر في محرك Qwen؟',
        answer: 'الترجمات المفضّلة فقط، وتُكتب بالطريقة نفسها: source=target، واحدة في كل سطر، في اتجاه واحد. لا توجد في Qwen قائمة مصطلحات ولا خلفية للجلسة، لذا يعرض قسم «مفردات مخصصة» لديه ذلك الحقل وحده.',
      },
    ],
  },
  troubleshooting: {
    title: 'استكشاف الأخطاء وإصلاحها',
    content:
      'هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const pt: TutorialData = {
  pageTitle: 'Vocabulário personalizado e contexto da sessão',
  backLink: 'Voltar ao início rápido com KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Visão geral',
    content:
      'Nomes, nomes de produtos e jargões são o que a tradução em tempo real mais erra. A KizunaAI permite que você diga, antes da reunião, quais palavras esperar, como traduzir termos específicos e sobre o que é a reunião. Os três campos são opcionais, ficam em <strong>Configurações → Avançado → Provedor</strong> e valem a partir da próxima sessão. Eles pertencem ao mecanismo Soniox; o mecanismo Qwen tem só Traduções preferidas (veja as perguntas frequentes). O exemplo é uma chamada entre 中文 e English.',
  },
  steps: [
    {
      title: 'Abra os campos',
      content:
        'Abra <strong>Configurações</strong>, mude para <strong>Avançado</strong> com o botão no canto superior direito do painel e escolha a aba <strong>Provedor</strong>. Role até <strong>Vocabulário personalizado</strong> e <strong>Contexto da sessão</strong>.',
      screenshot: '01-vocabulary-and-background.png',
    },
    {
      title: 'Liste os termos',
      content:
        'Em <strong>Termos</strong>, escreva um nome ou termo por linha: pessoas, empresas, produtos, siglas e jargões. Isso ajuda o reconhecimento a captar palavras que, de outra forma, ele entenderia errado.',
    },
    {
      title: 'Defina traduções preferidas',
      content:
        'Em <strong>Traduções preferidas</strong>, escreva <code>source=target</code>, uma por linha, para dizer como um termo deve ser traduzido, por exemplo <code>季度复盘=quarterly review</code>. É uma preferência, não uma substituição garantida: nomes com uma tradução consagrada funcionam melhor, enquanto palavras comuns podem manter a redação do próprio modelo.',
      tip: 'As entradas funcionam em um só sentido. No modo Ambos, adicione também a linha inversa (quarterly review=季度复盘) se quiser que o termo seja tratado nos dois sentidos.',
    },
    {
      title: 'Descreva a reunião',
      content:
        'Em <strong>Contexto da sessão</strong>, cole a pauta, o tema ou algumas notas sobre a reunião. O contexto ajuda o reconhecimento e a tradução a acompanhar o assunto.',
    },
    {
      title: 'Inicie a sessão',
      content:
        'Clique em <strong>Iniciar</strong>. O vocabulário e o contexto valem a partir da próxima sessão que você iniciar; uma sessão já em andamento mantém o que tinha ao começar.',
    },
  ],
  tips: {
    title: 'Dicas',
    items: [
      'Mantenha as listas curtas e específicas da reunião: algumas dezenas de nomes reais ajudam mais do que um glossário longo e genérico.',
      'Cada campo aceita até 4.000 caracteres. Se o contexto combinado ficar grande demais, o contexto da sessão é encurtado primeiro.',
      'Os campos ficam salvos neste dispositivo, então a próxima reunião já começa com eles. Atualize-os antes de cada reunião.',
    ],
  },
  faq: {
    title: 'Perguntas frequentes',
    items: [
      {
        question: 'Por que a minha tradução preferida não foi usada?',
        answer:
          'As traduções preferidas orientam o modelo em vez de substituir palavras mecanicamente. Nomes consagrados funcionam melhor; palavras muito comuns podem manter a redação do próprio modelo. Confira se a linha está escrita no sentido que está sendo traduzido.',
      },
      {
        question: 'Preciso dos dois campos?',
        answer: 'Não. Cada um ajuda sozinho. Os termos corrigem o reconhecimento de palavras específicas; o contexto ajuda com o assunto como um todo.',
      },
      {
        question: 'Quais destes o mecanismo Qwen tem?',
        answer: 'Só Traduções preferidas, escritas do mesmo jeito: source=target, uma por linha, em um só sentido. A Qwen não tem lista de Termos nem Contexto da sessão, então o bloco Vocabulário personalizado mostra só esse campo.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solução de problemas',
    content:
      'Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Você também pode perguntar no <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ru: TutorialData = {
  pageTitle: 'Пользовательский словарь и контекст сеанса',
  backLink: 'Назад к быстрому старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Обзор',
    content:
      'Имена, названия продуктов и профессиональный жаргон чаще всего переводятся в реальном времени с ошибками. KizunaAI позволяет заранее, до встречи, указать, какие слова ожидать, как переводить конкретные термины и о чём встреча. Все три поля необязательны, находятся в разделе <strong>Настройки → Расширенный → Поставщик</strong> и действуют со следующего сеанса. Они относятся к движку Soniox; у движка Qwen есть только Предпочтительные переводы (см. «Часто задаваемые вопросы»). В примере — звонок между 中文 и English.',
  },
  steps: [
    {
      title: 'Откройте поля',
      content:
        'Откройте <strong>Настройки</strong>, переключитесь в режим <strong>Расширенный</strong> кнопкой в правом верхнем углу панели и выберите вкладку <strong>Поставщик</strong>. Прокрутите до разделов <strong>Пользовательский словарь</strong> и <strong>Контекст сеанса</strong>.',
      screenshot: '01-vocabulary-and-background.png',
    },
    {
      title: 'Перечислите термины',
      content:
        'В поле <strong>Термины</strong> напишите по одному имени или термину на строку: люди, компании, продукты, аббревиатуры и жаргон. Это помогает распознаванию уловить слова, которые иначе были бы расслышаны неверно.',
    },
    {
      title: 'Задайте предпочтительные переводы',
      content:
        'В поле <strong>Предпочтительные переводы</strong> пишите <code>source=target</code>, по одной паре на строку, чтобы указать, как переводить термин, например <code>季度复盘=quarterly review</code>. Это предпочтение, а не гарантированная замена: лучше всего работают имена с устоявшимся переводом, а для обычных слов модель может оставить собственную формулировку.',
      tip: 'Записи действуют в одном направлении. В режиме «Оба» добавьте и обратную строку (quarterly review=季度复盘), если термин должен обрабатываться в обе стороны.',
    },
    {
      title: 'Опишите встречу',
      content:
        'В поле <strong>Контекст сеанса</strong> вставьте повестку, тему или несколько заметок о встрече. Контекст помогает распознаванию и переводу следить за предметом разговора.',
    },
    {
      title: 'Начните сеанс',
      content:
        'Нажмите <strong>Старт</strong>. Словарь и контекст действуют с того сеанса, который вы начнёте следующим; уже запущенный сеанс сохраняет то, с чем был начат.',
    },
  ],
  tips: {
    title: 'Советы',
    items: [
      'Делайте списки короткими и относящимися к конкретной встрече: несколько десятков реальных имён помогают больше, чем длинный общий глоссарий.',
      'Каждое поле вмещает до 4000 символов. Если общий контекст слишком велик, первым сокращается контекст сеанса.',
      'Поля сохраняются на этом устройстве, поэтому следующая встреча начнётся с ними. Обновляйте их перед каждой встречей.',
    ],
  },
  faq: {
    title: 'Часто задаваемые вопросы',
    items: [
      {
        question: 'Почему мой предпочтительный перевод не был использован?',
        answer:
          'Предпочтительные переводы направляют модель, а не заменяют слова механически. Лучше всего работают устоявшиеся имена; для очень распространённых слов модель может оставить собственную формулировку. Проверьте, что строка записана в том направлении, в котором идёт перевод.',
      },
      {
        question: 'Нужны ли оба поля?',
        answer: 'Нет. Каждое помогает и само по себе. Термины исправляют распознавание конкретных слов; контекст помогает с темой в целом.',
      },
      {
        question: 'Что из этого есть у движка Qwen?',
        answer: 'Только Предпочтительные переводы, записываемые так же: source=target, по одному на строку, в одном направлении. В Qwen нет списка терминов и контекста сеанса, поэтому в его блоке «Пользовательский словарь» показано только это поле.',
      },
    ],
  },
  troubleshooting: {
    title: 'Устранение неполадок',
    content:
      'Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Также можно задать вопрос в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const de: TutorialData = {
  pageTitle: 'Benutzerdefiniertes Vokabular und Sitzungshintergrund',
  backLink: 'Zurück zum KizunaAI-Schnellstart',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Übersicht',
    content:
      'Namen, Produktnamen und Fachjargon sind das, was Echtzeitübersetzung am häufigsten falsch macht. Mit KizunaAI können Sie vor dem Meeting angeben, welche Wörter zu erwarten sind, wie bestimmte Begriffe übersetzt werden sollen und worum es im Meeting geht. Alle drei Felder sind optional, befinden sich unter <strong>Einstellungen → Erweitert → Anbieter</strong> und gelten ab der nächsten Sitzung. Sie gehören zur Soniox-Engine; die Qwen-Engine hat nur Bevorzugte Übersetzungen (siehe die FAQ). Das Beispiel ist ein Gespräch zwischen 中文 und English.',
  },
  steps: [
    {
      title: 'Felder öffnen',
      content:
        'Öffnen Sie die <strong>Einstellungen</strong>, wechseln Sie mit der Schaltfläche oben rechts im Bereich zu <strong>Erweitert</strong> und wählen Sie den Tab <strong>Anbieter</strong>. Scrollen Sie zu <strong>Benutzerdefiniertes Vokabular</strong> und <strong>Sitzungshintergrund</strong>.',
      screenshot: '01-vocabulary-and-background.png',
    },
    {
      title: 'Begriffe auflisten',
      content:
        'Schreiben Sie unter <strong>Begriffe</strong> einen Namen oder Begriff pro Zeile: Personen, Firmen, Produkte, Abkürzungen und Fachjargon. So erkennt die Spracherkennung Wörter, die sie sonst falsch verstehen würde.',
    },
    {
      title: 'Bevorzugte Übersetzungen festlegen',
      content:
        'Schreiben Sie unter <strong>Bevorzugte Übersetzungen</strong> <code>source=target</code>, eine Zeile pro Eintrag, um festzulegen, wie ein Begriff übersetzt werden soll, zum Beispiel <code>季度复盘=quarterly review</code>. Das ist eine Präferenz, keine garantierte Ersetzung: Namen mit einer etablierten Übersetzung funktionieren am besten, bei gängigen Wörtern kann das Modell bei seiner eigenen Formulierung bleiben.',
      tip: 'Einträge gelten in eine Richtung. Fügen Sie im Modus „Beides“ auch die umgekehrte Zeile hinzu (quarterly review=季度复盘), wenn der Begriff in beide Richtungen berücksichtigt werden soll.',
    },
    {
      title: 'Das Meeting beschreiben',
      content:
        'Fügen Sie unter <strong>Sitzungshintergrund</strong> die Tagesordnung, das Thema oder ein paar Notizen zum Meeting ein. Der Kontext hilft Spracherkennung und Übersetzung, dem Thema zu folgen.',
    },
    {
      title: 'Sitzung starten',
      content:
        'Klicken Sie auf <strong>Starten</strong>. Vokabular und Hintergrund gelten ab der Sitzung, die Sie als Nächstes starten; eine bereits laufende Sitzung behält, womit sie gestartet wurde.',
    },
  ],
  tips: {
    title: 'Tipps',
    items: [
      'Halten Sie die Listen kurz und auf das Meeting zugeschnitten: Ein paar Dutzend echte Namen helfen mehr als ein langes allgemeines Glossar.',
      'Jedes Feld fasst bis zu 4.000 Zeichen. Wird der gesamte Kontext zu groß, wird zuerst der Sitzungshintergrund gekürzt.',
      'Die Felder werden auf diesem Gerät gespeichert, sodass das nächste Meeting mit ihnen beginnt. Aktualisieren Sie sie vor jedem Meeting.',
    ],
  },
  faq: {
    title: 'Häufig gestellte Fragen',
    items: [
      {
        question: 'Warum wurde meine bevorzugte Übersetzung nicht verwendet?',
        answer:
          'Bevorzugte Übersetzungen lenken das Modell, statt Wörter mechanisch zu ersetzen. Etablierte Namen funktionieren am besten; bei sehr gängigen Wörtern kann das Modell bei seiner eigenen Formulierung bleiben. Prüfen Sie, ob die Zeile in der Richtung geschrieben ist, in die übersetzt wird.',
      },
      {
        question: 'Brauche ich beide Felder?',
        answer: 'Nein. Jedes hilft auch für sich allein. Begriffe verbessern die Erkennung bestimmter Wörter; der Hintergrund hilft beim Thema als Ganzem.',
      },
      {
        question: 'Welche davon hat die Qwen-Engine?',
        answer: 'Nur Bevorzugte Übersetzungen, auf dieselbe Weise geschrieben: source=target, ein Eintrag pro Zeile, in eine Richtung. Qwen hat weder eine Liste „Begriffe“ noch einen Sitzungshintergrund, deshalb zeigt sein Block „Benutzerdefiniertes Vokabular“ nur dieses eine Feld.',
      },
    ],
  },
  troubleshooting: {
    title: 'Fehlerbehebung',
    content:
      'Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Sie können auch in den <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> fragen.',
  },
};

const uk: TutorialData = {
  pageTitle: 'Власний словник і тло сеансу',
  backLink: 'Назад до швидкого старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Огляд',
    content:
      'Імена, назви продуктів і професійний жаргон найчастіше перекладаються в реальному часі з помилками. KizunaAI дає змогу ще до зустрічі вказати, яких слів очікувати, як перекладати конкретні терміни і про що зустріч. Усі три поля необов’язкові, розташовані в розділі <strong>Налаштування → Розширений → Постачальник</strong> і діють з наступного сеансу. Вони стосуються рушія Soniox; у рушія Qwen є лише «Бажані переклади» (див. запитання нижче). У прикладі — дзвінок між 中文 і English.',
  },
  steps: [
    {
      title: 'Відкрийте поля',
      content:
        'Відкрийте <strong>Налаштування</strong>, перейдіть у режим <strong>Розширений</strong> кнопкою в правому верхньому куті панелі та виберіть вкладку <strong>Постачальник</strong>. Прокрутіть до розділів <strong>Власний словник</strong> і <strong>Тло сеансу</strong>.',
      screenshot: '01-vocabulary-and-background.png',
    },
    {
      title: 'Перелічіть терміни',
      content:
        'У полі <strong>Терміни</strong> напишіть по одному імені чи терміну на рядок: люди, компанії, продукти, абревіатури й жаргон. Це допомагає розпізнаванню вловити слова, які інакше були б почуті неправильно.',
    },
    {
      title: 'Задайте бажані переклади',
      content:
        'У полі <strong>Бажані переклади</strong> пишіть <code>source=target</code>, по одній парі на рядок, щоб вказати, як перекладати термін, наприклад <code>季度复盘=quarterly review</code>. Це побажання, а не гарантована заміна: найкраще працюють імена з усталеним перекладом, а для звичайних слів модель може залишити власне формулювання.',
      tip: 'Записи діють в одному напрямку. У режимі «Обидва» додайте й зворотний рядок (quarterly review=季度复盘), якщо термін має оброблятися в обидва боки.',
    },
    {
      title: 'Опишіть зустріч',
      content:
        'У поле <strong>Тло сеансу</strong> вставте порядок денний, тему або кілька нотаток про зустріч. Контекст допомагає розпізнаванню й перекладу стежити за предметом розмови.',
    },
    {
      title: 'Почніть сеанс',
      content:
        'Натисніть <strong>Почати</strong>. Словник і тло діють із того сеансу, який ви почнете наступним; уже запущений сеанс зберігає те, з чим його було розпочато.',
    },
  ],
  tips: {
    title: 'Поради',
    items: [
      'Робіть списки короткими й прив’язаними до конкретної зустрічі: кілька десятків реальних імен допомагають більше, ніж довгий загальний глосарій.',
      'Кожне поле вміщує до 4000 символів. Якщо загальний контекст завеликий, першим скорочується тло сеансу.',
      'Поля зберігаються на цьому пристрої, тож наступна зустріч почнеться з ними. Оновлюйте їх перед кожною зустріччю.',
    ],
  },
  faq: {
    title: 'Часті запитання',
    items: [
      {
        question: 'Чому мій бажаний переклад не було використано?',
        answer:
          'Бажані переклади спрямовують модель, а не замінюють слова механічно. Найкраще працюють усталені імена; для дуже поширених слів модель може залишити власне формулювання. Перевірте, що рядок записано в тому напрямку, у якому йде переклад.',
      },
      {
        question: 'Чи потрібні обидва поля?',
        answer: 'Ні. Кожне допомагає й саме собою. Терміни виправляють розпізнавання конкретних слів; тло допомагає з темою загалом.',
      },
      {
        question: 'Що з цього є в рушія Qwen?',
        answer: 'Лише «Бажані переклади», які записуються так само: source=target, по одному на рядок, в одному напрямку. У Qwen немає ні списку термінів, ні тла сеансу, тому в його блоці «Власний словник» показано лише це одне поле.',
      },
    ],
  },
  troubleshooting: {
    title: 'Усунення несправностей',
    content:
      'Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Також можна запитати в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const it: TutorialData = {
  pageTitle: 'Vocabolario personalizzato e contesto della sessione',
  backLink: 'Torna alla guida rapida a KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Panoramica',
    content:
      'Nomi, nomi di prodotti e gergo tecnico sono ciò che la traduzione in tempo reale sbaglia più spesso. KizunaAI ti permette di indicare, prima della riunione, quali parole aspettarsi, come tradurre termini specifici e di cosa tratta la riunione. Tutti e tre i campi sono facoltativi, si trovano in <strong>Impostazioni → Avanzato → Fornitore</strong> e valgono dalla sessione successiva. Appartengono al motore Soniox; il motore Qwen ha solo Traduzioni preferite (vedi le domande frequenti). L’esempio è una chiamata tra 中文 e English.',
  },
  steps: [
    {
      title: 'Apri i campi',
      content:
        'Apri <strong>Impostazioni</strong>, passa ad <strong>Avanzato</strong> con il pulsante in alto a destra del pannello e scegli la scheda <strong>Fornitore</strong>. Scorri fino a <strong>Vocabolario personalizzato</strong> e <strong>Contesto della sessione</strong>.',
      screenshot: '01-vocabulary-and-background.png',
    },
    {
      title: 'Elenca i termini',
      content:
        'In <strong>Termini</strong>, scrivi un nome o un termine per riga: persone, aziende, prodotti, sigle e gergo. Questo aiuta il riconoscimento a cogliere parole che altrimenti fraintenderebbe.',
    },
    {
      title: 'Imposta le traduzioni preferite',
      content:
        'In <strong>Traduzioni preferite</strong>, scrivi <code>source=target</code>, una per riga, per indicare come va tradotto un termine, per esempio <code>季度复盘=quarterly review</code>. È una preferenza, non una sostituzione garantita: i nomi con una resa consolidata funzionano meglio, mentre per le parole comuni il modello può mantenere la propria formulazione.',
      tip: 'Le voci valgono in una sola direzione. In modalità Entrambi, aggiungi anche la riga inversa (quarterly review=季度复盘) se vuoi che il termine sia gestito in entrambi i sensi.',
    },
    {
      title: 'Descrivi la riunione',
      content:
        'In <strong>Contesto della sessione</strong>, incolla l’ordine del giorno, l’argomento o qualche nota sulla riunione. Il contesto aiuta riconoscimento e traduzione a seguire l’argomento.',
    },
    {
      title: 'Avvia la sessione',
      content:
        'Fai clic su <strong>Avvia</strong>. Vocabolario e contesto valgono dalla sessione che avvii dopo; una sessione già attiva mantiene ciò con cui è partita.',
    },
  ],
  tips: {
    title: 'Consigli',
    items: [
      'Mantieni gli elenchi brevi e specifici per la riunione: qualche decina di nomi reali aiuta più di un lungo glossario generico.',
      'Ogni campo contiene fino a 4.000 caratteri. Se il contesto complessivo è troppo grande, viene accorciato per primo il contesto della sessione.',
      'I campi vengono salvati su questo dispositivo, quindi la riunione successiva parte con questi valori. Aggiornali prima di ogni riunione.',
    ],
  },
  faq: {
    title: 'Domande frequenti',
    items: [
      {
        question: 'Perché la mia traduzione preferita non è stata usata?',
        answer:
          'Le traduzioni preferite orientano il modello invece di sostituire le parole meccanicamente. I nomi consolidati funzionano meglio; per le parole molto comuni il modello può mantenere la propria formulazione. Verifica che la riga sia scritta nella direzione in cui si traduce.',
      },
      {
        question: 'Servono entrambi i campi?',
        answer: 'No. Ognuno aiuta anche da solo. I termini correggono il riconoscimento di parole specifiche; il contesto aiuta con l’argomento nel suo insieme.',
      },
      {
        question: 'Quali di questi ha il motore Qwen?',
        answer: 'Solo Traduzioni preferite, scritte allo stesso modo: source=target, una per riga, in una sola direzione. Qwen non ha né l’elenco Termini né il Contesto della sessione, quindi il suo blocco Vocabolario personalizzato mostra solo quel campo.',
      },
    ],
  },
  troubleshooting: {
    title: 'Risoluzione dei problemi',
    content:
      'Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Puoi anche chiedere su <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function KizunaVocabularyGuide() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/tutorials/kizuna-ai-vocabulary" />;
}
